import { useState, useEffect } from "react";
import { ContentLayout } from "@/components/layout/ContentLayout";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Button } from "@/components/ui/button";
import { useToast } from "@/components/ui/use-toast";
import { useNavigate, useParams } from "react-router-dom";
import { supabase } from "@/integrations/supabase/client";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Database } from "@/types/supabase";

const languageFlags = {
  tr: "🇹🇷",
  en: "🇬🇧",
  ar: "🇸🇦"
};

type Category = Database['public']['Tables']['categories']['Row'];

export default function EditCategory() {
  const [title, setTitle] = useState({
    tr: "",
    en: "",
    ar: ""
  });
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [isLoading, setIsLoading] = useState(true);
  
  const { toast } = useToast();
  const navigate = useNavigate();
  const { id } = useParams();

  useEffect(() => {
    async function fetchCategory() {
      try {
        setIsLoading(true);
        const { data, error } = await supabase
          .from('categories')
          .select('*')
          .eq('id', id)
          .single();
        
        if (error) throw error;
        
        if (data) {
          setTitle({
            tr: data.name_tr,
            en: data.name_en,
            ar: data.name_ar
          });
        }
      } catch (error) {
        console.error('Error fetching category:', error);
        toast({
          title: "Hata",
          description: "Kategori bilgileri yüklenirken bir hata oluştu.",
          variant: "destructive",
        });
        navigate("/portfolio/categories");
      } finally {
        setIsLoading(false);
      }
    }

    if (id) {
      fetchCategory();
    }
  }, [id, toast, navigate]);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setIsSubmitting(true);
    
    try {
      const { error } = await supabase
        .from('categories')
        .update({
          name_tr: title.tr,
          name_en: title.en,
          name_ar: title.ar
        })
        .eq('id', id);
      
      if (error) throw error;
      
      toast({
        title: "Başarılı",
        description: "Kategori başarıyla güncellendi.",
      });
      
      navigate("/portfolio/categories");
    } catch (error) {
      console.error('Error updating category:', error);
      toast({
        title: "Hata",
        description: "Kategori güncellenirken bir hata oluştu.",
        variant: "destructive",
      });
    } finally {
      setIsSubmitting(false);
    }
  };

  if (isLoading) {
    return (
      <ContentLayout 
        title="Kategori Düzenle" 
        backLink="/portfolio/categories"
      >
        <div className="flex justify-center p-8">
          <p>Kategori bilgileri yükleniyor...</p>
        </div>
      </ContentLayout>
    );
  }

  return (
    <ContentLayout 
      title="Kategori Düzenle" 
      backLink="/portfolio/categories"
    >
      <form onSubmit={handleSubmit} className="p-6 space-y-6">
        <Tabs defaultValue="tr" className="w-full">
          <TabsList className="grid w-full grid-cols-3">
            <TabsTrigger value="tr">{languageFlags.tr} Türkçe</TabsTrigger>
            <TabsTrigger value="en">{languageFlags.en} English</TabsTrigger>
            <TabsTrigger value="ar">{languageFlags.ar} العربية</TabsTrigger>
          </TabsList>
          <TabsContent value="tr" className="space-y-4">
            <div className="space-y-2">
              <Label htmlFor="title-tr">Kategori Adı (Türkçe)</Label>
              <Input
                id="title-tr"
                placeholder="Kategori adını girin"
                value={title.tr}
                onChange={(e) => setTitle({ ...title, tr: e.target.value })}
                required
              />
            </div>
          </TabsContent>
          <TabsContent value="en" className="space-y-4">
            <div className="space-y-2">
              <Label htmlFor="title-en">Kategori Adı (English)</Label>
              <Input
                id="title-en"
                placeholder="Enter category name"
                value={title.en}
                onChange={(e) => setTitle({ ...title, en: e.target.value })}
                required
              />
            </div>
          </TabsContent>
          <TabsContent value="ar" className="space-y-4">
            <div className="space-y-2">
              <Label htmlFor="title-ar">Kategori Adı (العربية)</Label>
              <Input
                id="title-ar"
                placeholder="أدخل اسم الفئة"
                value={title.ar}
                onChange={(e) => setTitle({ ...title, ar: e.target.value })}
                required
              />
            </div>
          </TabsContent>
        </Tabs>
        
        <div className="flex justify-end gap-2">
          <Button 
            type="button" 
            variant="outline" 
            onClick={() => navigate("/portfolio/categories")}
          >
            İptal
          </Button>
          <Button type="submit" disabled={isSubmitting}>
            {isSubmitting ? "Kaydediliyor..." : "Kaydet"}
          </Button>
        </div>
      </form>
    </ContentLayout>
  );
} 